import { Check } from 'lucide-react';

import type { IFunnelStep } from '@/lib/onboarding-funnel';
import { cn } from '@/lib/utils';

/**
 * Numbered stepper for the sticky bar. Display-only — the first step creates
 * the workspace, so "going back" to it would mean creating another one; the
 * footer's own Back button is the only sanctioned way to move.
 *
 * A screen passes the step it *is*, not a position: an index would silently
 * point at the wrong label the day the funnel gains or loses a step.
 *
 * Generic over the step key so the subscribe funnel's two-step bar and its
 * one-step free variant can both render through the same component.
 */
export function WizardStepper<K extends string>({
    steps,
    currentKey,
}: {
    /** Which funnel this bar describes. */
    steps: readonly IFunnelStep<K>[];
    currentKey: K;
}) {
    const current = steps.findIndex((step) => step.key === currentKey);
    return (
        <ol aria-label="Onboarding progress" className="m-0 flex list-none items-center p-0">
            {steps.map((step, index) => {
                const done = index < current;
                const active = index === current;
                return (
                    <li
                        key={step.key}
                        aria-current={active ? 'step' : undefined}
                        className="flex items-center gap-2.5 px-1 py-1.5 [&:not(:first-child)]:flex-1"
                    >
                        {index > 0 && (
                            <span
                                aria-hidden
                                className={cn(
                                    'mx-3 h-px min-w-6 flex-1',
                                    index <= current ? 'bg-primary/25' : 'bg-border'
                                )}
                            />
                        )}
                        <span
                            className={cn(
                                'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold transition-colors',
                                active && 'bg-primary text-primary-foreground',
                                done && 'bg-primary/10 text-primary',
                                !active && !done && 'border border-border text-muted-foreground'
                            )}
                        >
                            {done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : index + 1}
                        </span>
                        <span
                            className={cn(
                                'text-[13.5px] whitespace-nowrap',
                                active ? 'font-medium text-foreground' : 'text-muted-foreground',
                                !active && !done && 'text-muted-foreground/70'
                            )}
                        >
                            {step.label}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}
